export const CREDIT_REASONS = {
  incorrect_price: 'Incorrect price / Overcharged',
  overcharged: 'Overcharged',
  missing_goods: 'Missing goods',
  damaged_goods: 'Damaged goods',
  wrong_quantity: 'Wrong quantity',
  wrong_product: 'Wrong product',
  other: 'Other',
}

export function creditMoney(pennies) {
  return pennies == null || pennies === '' || !Number.isFinite(Number(pennies))
    ? '—'
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(Number(pennies) / 100)
}

/** "£20.00 on PO1643180519 and £32.47 on PO2993771279" */
export function describeSplitShares(slices = []) {
  const parts = (slices || [])
    .filter(slice => String(slice?.purchaseOrderNumber || '').trim())
    .map(
      slice =>
        `${creditMoney(slice.amountPennies)} on ${String(
          slice.purchaseOrderNumber,
        ).trim()}`,
    )
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

export function hasOutstandingInvoiceCredit(invoice = {}) {
  const credit = invoice.creditDiscrepancy
  return Boolean(
    credit &&
      (['credit_requested', 'partially_credited'].includes(credit.status) ||
        Number(credit.outstandingPennies) > 0),
  )
}

/**
 * Whether the Matching column should show Review difference.
 * Hide on clean Perfect match / Linked rows with no financial discrepancy signal.
 * Possible-duplicate rows use View existing instead (unless credit/acceptance exists).
 */
export function shouldShowInvoiceDiscrepancyReview(invoice = {}) {
  if (!invoice || invoice.documentType === 'creditNote') return false

  const hasCreditOrAcceptance =
    Boolean(invoice.differenceAcceptance?.acceptedAt) ||
    Number(invoice.creditDiscrepancy?.amountRequestedPennies) > 0 ||
    hasOutstandingInvoiceCredit(invoice)

  if (hasCreditOrAcceptance) return true

  // Duplicate resolution owns this column until the duplicate is dismissed.
  if (invoice.matchingStatus === 'possible_duplicate') return false

  if (invoice.matchingFailureReason === 'total_outside_tolerance') return true

  const differencePennies = invoice.matchSummary?.differencePennies
  const tolerancePennies = invoice.matchSummary?.tolerancePennies
  if (
    differencePennies != null &&
    differencePennies !== '' &&
    Number.isFinite(Number(differencePennies))
  ) {
    const tolerance = Math.max(0, Math.round(Number(tolerancePennies) || 0))
    if (Math.abs(Math.round(Number(differencePennies))) > tolerance) {
      return true
    }
  }

  return false
}

export function describeSupplierMismatchWarning(mismatch) {
  const invoiceSupplier = String(mismatch?.invoiceSupplier || '').trim()
  const orderSuppliers = (mismatch?.orderSuppliers || [])
    .map(name => String(name || '').trim())
    .filter(Boolean)
  if (!invoiceSupplier || !orderSuppliers.length) return ''
  const orders =
    orderSuppliers.length === 1
      ? orderSuppliers[0]
      : `${orderSuppliers.slice(0, -1).join(', ')} and ${
          orderSuppliers[orderSuppliers.length - 1]
        }`
  return `This invoice is from ${invoiceSupplier}. The purchase orders are from ${orders}.`
}

export function unsavedReceiptPurchaseOrders(summary) {
  const fromDisplay = Object.entries(
    summary?.receivedDisplayByPurchaseOrder || {},
  )
    .filter(([, row]) => row?.receiptSaved === false)
    .map(([purchaseOrderNumber]) => purchaseOrderNumber)
  if (fromDisplay.length) return fromDisplay
  return (summary?.allocationSlices || [])
    .filter(slice => slice?.receiptSaved === false)
    .map(slice => slice.purchaseOrderNumber)
}

export function creditRequestBlockReason(
  invoice = {},
  summary,
  canRequest = true,
) {
  if (!canRequest) return 'You do not have permission to request credit.'
  if (invoice.isArchived)
    return 'Archived invoices cannot have a new credit request.'
  if (invoice.documentType === 'creditNote')
    return 'Open the supplier invoice to request credit.'
  if (
    ['reconciled', 'sentToXero'].includes(invoice.status) ||
    invoice.poAllocations?.some(row => row.reconciledAt)
  ) {
    return 'This invoice is already wholly or partly reconciled. Review its reconciliation before requesting a credit.'
  }
  if (
    summary?.creditRequest ||
    Number(invoice.creditDiscrepancy?.amountRequestedPennies) > 0
  ) {
    return 'A credit request already exists. Review the credit received and remaining difference below.'
  }
  if (!summary?.comparison?.known)
    return 'The received value is unavailable. Link the correct order and check its saved receipt, then refresh. Keep a correct receipt unchanged.'
  if (unsavedReceiptPurchaseOrders(summary).length) {
    const pending = unsavedReceiptPurchaseOrders(summary)
    return `${pending.join(' and ')} ${
      pending.length === 1 ? 'has' : 'have'
    } not been receipted yet. Save the goods receipt before reviewing a credit.`
  }
  if ((summary?.allocationSlices || []).length >= 2) {
    const over = summary.allocationSlices.filter(slice => slice.overInvoiced)
    if (!over.length) {
      return 'Each purchase order share is at or below that order’s receipt. No supplier credit is due for the split.'
    }
  } else if (summary.comparison.underInvoiced)
    return 'The invoice is lower than the goods received. Review the difference; no supplier credit is due.'
  if (!summary.comparison.overInvoiced)
    return 'The invoice and received value match within tolerance. No credit request is needed.'
  if (!summary.linkedOrderCount)
    return 'Link the invoice to its order before requesting credit.'
  return ''
}

/**
 * When Accept & Reconcile is unavailable from Review difference.
 * Uses invoices access (not Credit Notes). Does not rewrite financials.
 */
export function acceptDifferenceBlockReason(
  invoice = {},
  summary,
  canAccept = true,
) {
  if (!canAccept) return 'You do not have permission to accept this difference.'
  if (invoice.isArchived)
    return 'Archived invoices cannot accept a reconciliation difference.'
  if (invoice.documentType === 'creditNote')
    return 'Open the supplier invoice to accept a difference.'
  if (
    invoice.differenceAcceptance?.acceptedAt ||
    summary?.differenceAcceptance?.acceptedAt
  ) {
    return 'This difference was already accepted and the invoice reconciled.'
  }
  if (
    ['reconciled', 'sentToXero'].includes(invoice.status) ||
    invoice.poAllocations?.some(row => row.reconciledAt)
  ) {
    return 'This invoice is already wholly or partly reconciled.'
  }
  if (hasOutstandingInvoiceCredit(invoice) || summary?.hasOpenCreditRequest) {
    return 'Resolve the outstanding credit request before accepting this difference.'
  }
  if (unsavedReceiptPurchaseOrders(summary).length) {
    const pending = unsavedReceiptPurchaseOrders(summary)
    return `${pending.join(' and ')} ${
      pending.length === 1 ? 'has' : 'have'
    } not been receipted yet. Save the goods receipt before accepting a difference.`
  }
  if (!summary?.comparison?.known)
    return 'The received value is unavailable. Link the correct order and check its saved receipt, then refresh.'
  if (
    summary.comparison.withinTolerance ||
    (!summary.comparison.overInvoiced && !summary.comparison.underInvoiced)
  ) {
    return 'The invoice and received value match within tolerance. Use normal reconcile instead.'
  }
  if (!summary.linkedOrderCount)
    return 'Link the invoice to its order before accepting the difference.'
  return ''
}

export function validateCreditAmount(amountPounds, differencePennies) {
  if (!Number.isFinite(differencePennies) || differencePennies <= 0)
    return 'Refresh the invoice difference before requesting credit.'
  if (!/^\d+(\.\d{1,2})?$/.test(String(amountPounds ?? '').trim()))
    return 'Enter an amount with up to two decimal places.'
  const pennies = Math.round(Number(amountPounds) * 100)
  if (!Number.isSafeInteger(pennies) || pennies <= 0)
    return 'Enter a credit amount greater than zero.'
  if (pennies > differencePennies)
    return 'Credit cannot exceed the invoice difference.'
  return ''
}
